import { Link } from "@tanstack/react-router";
import { ArrowUpRight, X } from "lucide-react";
import { Dialog as DialogPrimitive, Popover as PopoverPrimitive, Slot } from "radix-ui";
import { cloneElement, isValidElement, useEffect, useId, useRef, useState, type ReactElement, type ReactNode } from "react";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { Skeleton } from "./misc";

/* ==========================================================================
   Peek: a hover / focus card.
   - mouse: opens after a short intent delay, stays open while the pointer is on the trigger or the card
   - keyboard: opens on focus-visible (read-only preview, focus stays on the trigger);
     Enter/Space (or click) pins it and moves focus into the card (focus-trapped popover, Esc returns)
   - touch: a tap pins it
   - `onActivate`: give the trigger its own click/Enter action instead (e.g. open the QuickPanel);
     the card then stays a preview and the action is the way in.
   ========================================================================== */
export function Peek({
  children,
  content,
  label,
  side = "bottom",
  align = "start",
  openDelay = 280,
  closeDelay = 160,
  onActivate,
  className,
  disabled,
}: {
  children: ReactElement;
  /** Rendered lazily (only when open). */
  content: () => ReactNode;
  /** Accessible name of the card. */
  label: string;
  side?: "top" | "bottom" | "left" | "right";
  align?: "start" | "center" | "end";
  openDelay?: number;
  closeDelay?: number;
  onActivate?: () => void;
  className?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const contentRef = useRef<HTMLDivElement>(null);
  /** Where focus goes back to when a pinned card closes (the anchor is not a Radix Trigger, so we restore it ourselves). */
  const returnTo = useRef<HTMLElement | null>(null);
  const cardId = useId();
  useEffect(() => () => clearTimeout(timer.current), []);

  if (disabled || !isValidElement(children)) return children;
  const clear = () => clearTimeout(timer.current);
  const later = (fn: () => void, ms: number) => {
    clear();
    timer.current = setTimeout(fn, ms);
  };
  const show = () => later(() => setOpen(true), openDelay);
  const hide = () => {
    if (!pinned) later(() => setOpen(false), closeDelay);
  };
  const pin = () => {
    clear();
    returnTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setPinned(true);
    setOpen(true);
    // move focus into the card (it is a modal popover while pinned)
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const el = contentRef.current;
      (el?.querySelector<HTMLElement>("a[href],button:not([disabled]),[tabindex]:not([tabindex='-1'])") ?? el)?.focus();
    }));
  };
  const child = children as ReactElement<Record<string, unknown>>;
  const prev = child.props;
  const call = (name: string, e: unknown) => (prev[name] as ((e: unknown) => void) | undefined)?.(e);
  const interactive = typeof child.type === "string" && (child.type === "button" || child.type === "a");

  const trigger = cloneElement(child, {
    "aria-describedby": open && !pinned ? cardId : (prev["aria-describedby"] as string | undefined),
    ...(interactive && !onActivate ? { "aria-expanded": open && pinned, "aria-haspopup": "dialog" } : {}),
    "data-peek-open": open ? "" : undefined,
    onPointerEnter: (e: React.PointerEvent) => {
      call("onPointerEnter", e);
      if (e.pointerType === "mouse") show();
    },
    onPointerLeave: (e: React.PointerEvent) => {
      call("onPointerLeave", e);
      if (e.pointerType === "mouse") hide();
    },
    onFocus: (e: React.FocusEvent<HTMLElement>) => {
      call("onFocus", e);
      if (e.currentTarget.matches(":focus-visible")) later(() => setOpen(true), 120);
    },
    onBlur: (e: React.FocusEvent) => {
      call("onBlur", e);
      hide();
    },
    onClick: (e: React.MouseEvent) => {
      call("onClick", e);
      if (e.defaultPrevented) return;
      clear();
      if (onActivate) {
        setOpen(false);
        onActivate();
        return;
      }
      e.preventDefault();
      if (open && pinned) {
        setOpen(false);
        setPinned(false);
      } else pin();
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      call("onKeyDown", e);
      if (e.key === "Escape" && open) {
        e.stopPropagation();
        setOpen(false);
        setPinned(false);
      }
    },
  });

  return (
    <PopoverPrimitive.Root
      modal={pinned}
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setPinned(false);
      }}
    >
      <PopoverPrimitive.Anchor asChild>{trigger}</PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          ref={contentRef}
          id={cardId}
          role={pinned ? "dialog" : "tooltip"}
          aria-label={label}
          side={side}
          align={align}
          sideOffset={8}
          collisionPadding={12}
          onOpenAutoFocus={(e) => {
            if (!pinned) e.preventDefault();
          }}
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            const back = returnTo.current;
            returnTo.current = null;
            if (back?.isConnected) back.focus();
          }}
          onPointerEnter={clear}
          onPointerLeave={(e) => e.pointerType === "mouse" && hide()}
          data-testid="peek-card"
          className={cn(
            "z-[65] w-[340px] max-w-[calc(100vw-1.5rem)] origin-[var(--radix-popover-content-transform-origin)] overflow-hidden rounded-card border border-line bg-surface shadow-pop outline-none animate-pop-in",
            className,
          )}
        >
          {content()}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

/** Standard peek-card layout: optional media, title row, body, and a footer action row. */
export function PeekCard({ media, title, meta, children, footer }: { media?: ReactNode; title: ReactNode; meta?: ReactNode; children?: ReactNode; footer?: ReactNode }) {
  return (
    <div>
      {media}
      <div className="space-y-2 p-3.5">
        <div className="min-w-0">
          <div className="truncate text-[13.5px] font-semibold text-ink">{title}</div>
          {meta && <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11.5px] text-ink-3">{meta}</div>}
        </div>
        {children}
      </div>
      {footer && <div className="flex items-center justify-end gap-1.5 border-t border-line bg-surface-2/50 px-3 py-2">{footer}</div>}
    </div>
  );
}

/* ==========================================================================
   QuickPanel: a side sheet for "look and edit without leaving the page".
   Radix Dialog (focus trap, Esc, inert page, return focus), slides in from the inline end
   (the left in Arabic), header actions + "Open full page".
   ========================================================================== */
export function QuickPanel({
  open,
  onOpenChange,
  title,
  subtitle,
  icon,
  actions,
  fullPage,
  children,
  footer,
  width = "md",
  loading,
  testId = "quick-panel",
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  /** Header actions (icon buttons, toggles). */
  actions?: ReactNode;
  /** Where "Open full page" goes (a path inside the app). */
  fullPage?: { to: string; params?: Record<string, string>; search?: Record<string, unknown>; label?: string };
  children: ReactNode;
  footer?: ReactNode;
  width?: "md" | "lg";
  loading?: boolean;
  testId?: string;
}) {
  const { t } = useT();
  const w = width === "lg" ? "sm:max-w-[640px]" : "sm:max-w-[520px]";
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-[color-mix(in_srgb,var(--overlay)_70%,transparent)] backdrop-blur-[1.5px] data-[state=open]:animate-fade-in" />
        <DialogPrimitive.Content
          data-testid={testId}
          aria-busy={loading || undefined}
          className={cn(
            "fixed inset-y-0 end-0 z-50 flex w-full flex-col border-s border-line bg-surface shadow-overlay outline-none sm:inset-y-2 sm:end-2 sm:rounded-sheet sm:border",
            "data-[state=open]:animate-[sheet-end_.34s_var(--ease-out)]",
            w,
          )}
        >
          <header className="flex items-start gap-3 border-b border-line px-5 pt-4 pb-3.5">
            {icon && <div className="grid size-10 shrink-0 place-items-center rounded-control bg-surface-2 text-[18px] text-ink-2 [&_svg]:size-[18px]">{icon}</div>}
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="truncate font-display text-[16.5px] font-semibold text-ink">{loading ? <Skeleton className="h-5 w-40" /> : title}</DialogPrimitive.Title>
              {subtitle ? <DialogPrimitive.Description className="mt-0.5 text-[12.5px] text-ink-3" asChild={typeof subtitle !== "string"}>{typeof subtitle === "string" ? subtitle : <div>{subtitle}</div>}</DialogPrimitive.Description> : <DialogPrimitive.Description className="sr-only">{typeof title === "string" ? title : t("quick.panel")}</DialogPrimitive.Description>}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {actions}
              {fullPage && (
                <Link
                  to={fullPage.to as never}
                  params={fullPage.params as never}
                  search={fullPage.search as never}
                  data-testid="open-full-page"
                  className="inline-flex h-8 items-center gap-1 rounded-control px-2.5 text-[12.5px] font-medium text-accent transition hover:bg-accent-soft"
                >
                  {fullPage.label ?? t("quick.openFull")}
                  <ArrowUpRight className="size-3.5 flip-rtl" />
                </Link>
              )}
              <DialogPrimitive.Close className="grid size-8 place-items-center rounded-control bg-surface-2 text-ink-2 transition hover:text-ink" aria-label={t("common.actions.close")}>
                <X className="size-4" />
              </DialogPrimitive.Close>
            </div>
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3">{footer}</footer>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** A labelled block inside a QuickPanel. */
export function PanelSection({ title, action, children, className }: { title: ReactNode; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn("mb-5 last:mb-0", className)}>
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-[11.5px] font-semibold uppercase tracking-wide text-ink-3">{title}</h3>
        {action && <div className="ms-auto">{action}</div>}
      </div>
      {children}
    </section>
  );
}

/** Wraps any element so it opens a peek of `kind:id` on click (keeps the element's own semantics). */
export function PeekTrigger({ children, onOpen }: { children: ReactElement; onOpen: () => void }) {
  return (
    <Slot.Root
      onClick={(e: React.MouseEvent) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return; // let "open in new tab" work on links
        e.preventDefault();
        onOpen();
      }}
    >
      {children}
    </Slot.Root>
  );
}
